import { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Check, AlertCircle } from 'lucide-react';
import { StatusBadge } from '../common';
import { Icon } from '../common/Icon';
import { DistrictMap } from '../common/DistrictMap';
import { calculateMilestoneInfo, loadClaimedMilestones, calculateTotalMilestoneEarnings } from '../../services/milestoneService';
import { calculateTotalEarnings } from '../../services/commissionService';
import { supabase } from '../../services/supabaseClient';
import { STATUS_META } from '../../utils/constants';
import { AppContext } from '../../context/AppContext';

// Add spinner animation
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
if (!document.querySelector('style[data-spinner]')) {
  styleSheet.setAttribute('data-spinner', 'true');
  document.head.appendChild(styleSheet);
}

/**
 * Marketer Dashboard
 * Shows milestone progress, deal stats, recent deals, and earnings
 */
export function MarketerDashboard({ currentUser, restaurants, userCommissions }) {
  const navigate = useNavigate();
  const ctx = useContext(AppContext);
  const [closedDealsCount, setClosedDealsCount] = useState(0);
  const [claimedMilestones, setClaimedMilestones] = useState([]);
  const [milestoneInfo, setMilestoneInfo] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);
  const [showMilestoneInfo, setShowMilestoneInfo] = useState(false);
  const [showPendingTopupInfo, setShowPendingTopupInfo] = useState(false);
  const [freeRegistrationsRemaining, setFreeRegistrationsRemaining] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [editedContactsCount, setEditedContactsCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Load milestone and deal data
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser || !restaurants) return;

      // Filter marketer's deals
      const myDeals = restaurants.filter(r => r.assignedTo === currentUser.id);
      const closedDeals = myDeals.filter(r => r.status === 'deal_closed');
      
      setClosedDealsCount(closedDeals.length);
      
      // Get recent 5 deals
      const sorted = [...myDeals].sort((a, b) => 
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      ).slice(0, 5);
      setRecentDeals(sorted);

      // Load milestones
      const claimed = await loadClaimedMilestones(currentUser.id);
      setClaimedMilestones(claimed);
      setMilestoneInfo(calculateMilestoneInfo(closedDeals.length));

      // Load free registrations remaining
      try {
        const { data, error } = await supabase
          .from('users')
          .select('free_registrations_remaining')
          .eq('id', currentUser.id)
          .single();
        
        if (error) throw error;
        setFreeRegistrationsRemaining(data?.free_registrations_remaining ?? 0);
      } catch (err) {
        console.error('Load free registrations error:', err);
      }

      // Calculate top 3 most requested products
      const categoryCount = {};
      myDeals.forEach(deal => {
        if (deal.interestedCategories && Array.isArray(deal.interestedCategories)) {
          deal.interestedCategories.forEach(cat => {
            categoryCount[cat] = (categoryCount[cat] || 0) + 1;
          });
        }
      });
      
      const sortedProducts = Object.entries(categoryCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name, count]) => ({ name, count }));
      
      setTopProducts(sortedProducts);

      // Count edited contacts (contacts with any registration data)
      const editedCount = myDeals.filter(deal => {
        return deal.ownerName || deal.ownerPhone || deal.taxNumber || deal.commissionPercentage;
      }).length;
      setEditedContactsCount(editedCount);
    };

    loadData();
  }, [currentUser, restaurants]);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Returned wrong info reports (need correction or were corrected by admin)
  const returnedWrongInfoReports = useMemo(() => {
    return (restaurants || []).filter(r => 
      r.assignedTo === currentUser.id && 
      r.wrongInfoFlag && 
      (r.wrongInfoStatus === 'returned' || r.wrongInfoStatus === 'corrected')
    );
  }, [restaurants, currentUser]);

  // Calculate stats
  const myDeals = useMemo(() => 
    restaurants?.filter(r => r.assignedTo === currentUser.id) || [],
    [restaurants, currentUser]
  );
  const totalDeals = myDeals.length;
  const closedDeals = myDeals.filter(r => r.status === 'deal_closed').length;
  const inProgress = myDeals.filter(r => r.status !== 'new' && r.status !== 'deal_closed').length;
  const newDeals = myDeals.filter(r => r.status === 'new').length;

  // Count restaurants per district for visualization
  const districtCounts = useMemo(() => {
    const counts = {};
    myDeals.forEach(deal => {
      const district = deal.area || 'غير محدد';
      counts[district] = (counts[district] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8); // Top 8 districts
  }, [myDeals]);

  // Pending top-up deals (closed but waiting for vendor's first top-up)
  const pendingTopupDeals = useMemo(() => 
    myDeals.filter(r => r.status === 'deal_closed' && r.commissionPending === true),
    [myDeals]
  );
  const pendingTopupCount = pendingTopupDeals.length;
  const potentialPendingEarnings = pendingTopupCount * 20; // 20 AED per deal
  
  const commissionEarnings = calculateTotalEarnings(userCommissions || []);
  const milestoneEarnings = calculateTotalMilestoneEarnings(claimedMilestones);
  const totalEarnings = commissionEarnings + milestoneEarnings;

  if (!milestoneInfo) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#000000' }}>جاري تحميل لوحة التحكم...</div>;
  }

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 32px)' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: '#000000', fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          🎯 لوحة التحكم
        </h1>
        <p style={{ color: '#000000', fontSize: 14 }}>
          مرحباً {currentUser.name}، إليك نظرة عامة على أدائك
        </p>
      </div>

      {/* Returned Wrong Info Reports Alert */}
      {returnedWrongInfoReports.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #1e3a5f, #ffffff)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 24,
          border: '2px solid #4ecdc4',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: '#4ecdc422',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icon name="ArrowRight" size={28} color="#4ecdc4" />
            </div>
            <div>
              <h2 style={{ color: '#60a5fa', fontSize: 18, fontWeight: 900, margin: '0 0 4px' }}>
                📝 معلومات تحتاج تصحيح
              </h2>
              <p style={{ color: '#93c5fd', fontSize: 13, margin: 0 }}>
                لديك {returnedWrongInfoReports.length} مطعم يحتاج تصحيح المعلومات والعودة للعمل عليه
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {returnedWrongInfoReports.map(report => (
              <div
                key={report.id}
                onClick={() => {
                  // Open contact details modal
                  if (ctx && ctx.setModal) {
                    ctx.setModal({ type: 'view_details', data: report });
                  }
                }}
                style={{
                  background: '#ffffff',
                  borderRadius: 10,
                  padding: 14,
                  border: '1px solid #4ecdc444',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#e2e8f0';
                  e.currentTarget.style.borderColor = '#4ecdc4';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#4ecdc444';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#000000', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                      🍽️ {report.name}
                    </div>
                    <div style={{ color: '#000000', fontSize: 12 }}>
                      نوع الخطأ: {
                        report.wrongInfoType === 'wrong_number' ? 'رقم هاتف خاطئ' :
                        report.wrongInfoType === 'wrong_name' ? 'اسم المطعم خاطئ' :
                        report.wrongInfoType === 'wrong_location' ? 'موقع خاطئ' :
                        report.wrongInfoType === 'duplicate' ? 'مكرر' :
                        report.wrongInfoType === 'closed' ? 'المطعم مغلق' :
                        report.wrongInfoType || 'غير محدد'
                      }
                    </div>
                  </div>
                  <span style={{
                    background: report.wrongInfoStatus === 'corrected' ? '#10b98122' : '#4ecdc422',
                    color: report.wrongInfoStatus === 'corrected' ? '#10b981' : '#60a5fa',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}>
                    {report.wrongInfoStatus === 'corrected' ? '✅ تم التصحيح' : 'يحتاج تصحيح'}
                  </span>
                </div>
                
                {report.wrongInfoAdminNote && (
                  <div style={{
                    background: '#ffffff',
                    borderRadius: 8,
                    padding: 10,
                    marginTop: 8,
                    borderRight: '3px solid #4ecdc4',
                  }}>
                    <div style={{ color: '#60a5fa', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                      💬 ملاحظة الإدارة:
                    </div>
                    <div style={{ color: '#000000', fontSize: 12 }}>
                      {report.wrongInfoAdminNote}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 16,
            padding: 12,
            background: '#4ecdc411',
            borderRadius: 8,
            border: '1px solid #4ecdc444',
          }}>
            <p style={{ color: '#93c5fd', fontSize: 12, margin: 0 }}>
              💡 <strong>ملاحظة:</strong> يرجى تصحيح المعلومات والعودة للعمل على هذه المطاعم. يمكنك فتح تفاصيل المطعم وتحديث المعلومات الصحيحة.
            </p>
          </div>
        </div>
      )}

      {/* Milestone Progress Bar */}
      <div style={{
        background: 'linear-gradient(135deg, #ffffff, #0f172a)',
        borderRadius: 16,
        padding: 24,
        marginBottom: 24,
        border: '2px solid #4ecdc4',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div>
              <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, margin: 0 }}>
                🎮 مكافآت الإنجازات
              </h3>
              <p style={{ color: '#000000', fontSize: 13, margin: '4px 0 0' }}>
                {milestoneInfo.closedDeals} صفقة مغلقة
              </p>
            </div>
            <button
              onClick={() => setShowMilestoneInfo(!showMilestoneInfo)}
              style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid #4ecdc4',
                borderRadius: 8,
                padding: '6px 12px',
                color: '#60a5fa',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
              title="شرح نظام المكافآت"
            >
              ℹ️ مكافأة مالية
            </button>
          </div>
          {milestoneInfo.nextMilestone && (
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#64748b', fontSize: 12 }}>الهدف التالي</div>
              <div style={{ color: '#4ecdc4', fontSize: 20, fontWeight: 900 }}>
                {milestoneInfo.nextMilestone.deals} صفقة
              </div>
              <div style={{ color: '#10b981', fontSize: 14, fontWeight: 700 }}>
                +{milestoneInfo.nextMilestone.reward} AED
              </div>
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        <div style={{
          background: '#ffffff',
          borderRadius: 12,
          height: 32,
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid #334155',
        }}>
          <div style={{
            background: 'linear-gradient(90deg, #4ecdc4, #10b981)',
            height: '100%',
            width: `${milestoneInfo.progress}%`,
            transition: 'width 0.5s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 900,
            fontSize: 14,
          }}>
            {milestoneInfo.progress.toFixed(0)}%
          </div>
        </div>
        
        {/* Milestone Info Popup */}
        {showMilestoneInfo && (
          <div style={{
            marginTop: 16,
            padding: 20,
            background: 'rgba(59, 130, 246, 0.05)',
            borderRadius: 12,
            border: '1px solid #4ecdc4',
            maxHeight: '500px',
            overflowY: 'auto',
          }}>
            <h4 style={{ color: '#60a5fa', fontSize: 16, fontWeight: 900, marginBottom: 8, margin: '0 0 8px' }}>
              💰 جدول المكافآت المالية - حتى 100 صفقة
            </h4>
            <p style={{ color: '#000000', fontSize: 13, margin: '0 0 16px', lineHeight: 1.6 }}>
              🚀 كلما أغلقت المزيد من الصفقات، تحصل على مكافآت أكبر! إليك الجدول الكامل:
            </p>
            
            {/* Milestones Table */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 10,
              marginBottom: 16,
            }}>
              {(() => {
                // Generate all milestones up to 100 deals
                const allMilestones = [];
                for (let level = 0; level <= 9; level++) {
                  const deals = 5 + (level * 10);
                  const reward = 100 + (level * 50);
                  if (deals <= 100) {
                    allMilestones.push({ deals, reward, level });
                  }
                }
                
                return allMilestones.map((m, idx) => {
                  const isReached = milestoneInfo.closedDeals >= m.deals;
                  return (
                    <div key={idx} style={{
                      padding: 12,
                      background: isReached 
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1))'
                        : 'rgba(100, 116, 139, 0.08)',
                      borderRadius: 10,
                      border: `2px solid ${isReached ? '#10b981' : '#475569'}`,
                      textAlign: 'center',
                      position: 'relative',
                      boxShadow: isReached ? '0 0 15px rgba(16, 185, 129, 0.3)' : 'none',
                    }}>
                      {isReached && (
                        <div style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          background: '#10b981',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                        }}>
                          ✓
                        </div>
                      )}
                      <div style={{ color: isReached ? '#34d399' : '#94a3b8', fontSize: 14, fontWeight: 900, marginBottom: 4 }}>
                        {m.deals} صفقة
                      </div>
                      <div style={{ color: isReached ? '#10b981' : '#64748b', fontSize: 16, fontWeight: 900 }}>
                        +{m.reward} AED
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            {/* Total Rewards Summary */}
            <div style={{
              padding: 16,
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(59, 130, 246, 0.2))',
              borderRadius: 12,
              border: '2px solid #10b981',
              textAlign: 'center',
            }}>
              <div style={{ color: '#000000', fontSize: 13, marginBottom: 4 }}>
                💎 إجمالي المكافآت عند إغلاق 100 صفقة
              </div>
              <div style={{ color: '#34d399', fontSize: 36, fontWeight: 900 }}>
                3,250 AED
              </div>
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                مكافآت الإنجازات فقط (بالإضافة إلى العمولات)
              </div>
            </div>
          </div>
        )}
        
        {/* Milestone Markers */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginTop: 16,
          padding: '0 4px',
          gap: 8,
        }}>
          {milestoneInfo.allMilestones.slice(0, 5).map((m, idx) => {
            const isNext = !m.reached && (idx === 0 || milestoneInfo.allMilestones[idx - 1]?.reached);
            const isFuture = !m.reached && !isNext;
            
            return (
              <div key={idx} style={{ 
                textAlign: 'center',
                minWidth: 70,
                flex: 1,
                position: 'relative',
              }}>
                {/* Connecting Line */}
                {idx < 4 && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: '50%',
                    width: '100%',
                    height: 3,
                    background: m.reached ? '#10b981' : '#ffffff',
                    zIndex: 0,
                  }} />
                )}
                
                {/* Milestone Circle */}
                <div style={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  background: m.reached 
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : isNext
                      ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                      : 'linear-gradient(135deg, #374151, #4b5563)',
                  margin: '0 auto 6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  color: '#fff',
                  fontWeight: 900,
                  position: 'relative',
                  zIndex: 1,
                  border: m.reached ? '3px solid #34d399' : isNext ? '3px solid #fbbf24' : '2px solid #6b7280',
                  boxShadow: m.reached 
                    ? '0 0 20px rgba(16, 185, 129, 0.5)'
                    : isNext 
                      ? '0 0 20px rgba(245, 158, 11, 0.6), 0 0 40px rgba(245, 158, 11, 0.3)'
                      : '0 0 10px rgba(107, 114, 128, 0.2)',
                  animation: isNext ? 'pulse 2s infinite' : 'none',
                }}>
                  {m.reached ? '🏆' : m.deals}
                </div>
                
                {/* Deals Count */}
                <div style={{ 
                  color: m.reached ? '#34d399' : isNext ? '#fbbf24' : '#64748b', 
                  fontSize: 11,
                  fontWeight: m.reached || isNext ? 900 : 600,
                  marginBottom: 2,
                }}>
                  {m.deals} صفقة
                </div>
                
                {/* Reward Amount */}
                <div style={{ 
                  color: m.reached ? '#10b981' : isNext ? '#f59e0b' : '#475569', 
                  fontSize: 12, 
                  fontWeight: 900,
                  background: isNext ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                  padding: isNext ? '2px 6px' : '0',
                  borderRadius: isNext ? '4px' : '0',
                }}>
                  {m.reward} AED
                </div>
                
                {/* Status Label */}
                {m.reached && (
                  <div style={{
                    color: '#10b981',
                    fontSize: 9,
                    fontWeight: 700,
                    marginTop: 2,
                  }}>
                    ✓ تم
                  </div>
                )}
                {isNext && (
                  <div style={{
                    color: '#f59e0b',
                    fontSize: 9,
                    fontWeight: 700,
                    marginTop: 2,
                    animation: 'pulse 2s infinite',
                  }}>
                    🔥 الهدف
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pipeline Stage Counts */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: 8, 
        marginBottom: 32 
      }}>
        {Object.entries(STATUS_META).map(([key, meta]) => {
          const count = myDeals.filter(r => r.status === key).length;
          const isHighlighted = count > 0;
          return (
            <div 
              key={key} 
              style={{ 
                background: isHighlighted ? meta.bg : '#ffffff', 
                borderRadius: 11, 
                padding: '12px 14px',
                border: `1px solid ${isHighlighted ? meta.color + '66' : '#e2e8f0'}`, 
                textAlign: 'center',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 10, color: meta.color, fontWeight: 700, marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <Icon name={meta.icon} size={12} color={meta.color} />
                <span>{meta.label}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: isHighlighted ? meta.color : '#475569' }}>
                {count}
              </div>
            </div>
          );
        })}
      </div>

      {/* Free Registrations Card */}
      {freeRegistrationsRemaining !== null && (
        <div style={{
          background: freeRegistrationsRemaining > 0 
            ? 'linear-gradient(135deg, #10b98122, #05966911)' 
            : 'linear-gradient(135deg, #ef444422, #dc262611)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 32,
          border: `2px solid ${freeRegistrationsRemaining > 0 ? '#10b981' : '#ef4444'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: freeRegistrationsRemaining > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
            }}>
              {freeRegistrationsRemaining > 0 ? '🎁' : '⚠️'}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                color: freeRegistrationsRemaining > 0 ? '#10b981' : '#ef4444', 
                fontSize: 20, 
                fontWeight: 900, 
                margin: '0 0 8px' 
              }}>
                {freeRegistrationsRemaining > 0 
                  ? `التسجيلات المجانية: ${freeRegistrationsRemaining} متبقي` 
                  : 'نفذت التسجيلات المجانية!'}
              </h3>
              <p style={{ color: '#000000', fontSize: 14, margin: 0 }}>
                {freeRegistrationsRemaining > 0
                  ? `لديك ${freeRegistrationsRemaining} مطاعم يمكنك تسجيلها مجاناً (العميل لا يدفع)`
                  : 'يجب إقناع المطاعم بالدفع (150 أو 300 AED) - لا يمكنك التسجيل مجاناً'}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                color: freeRegistrationsRemaining > 0 ? '#10b981' : '#ef4444',
                fontSize: 48,
                fontWeight: 900,
                lineHeight: 1,
              }}>
                {freeRegistrationsRemaining}
              </div>
              <div style={{ color: '#000000', fontSize: 12, marginTop: 4 }}>
                من 5
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Top-up Deals Container */}
      {pendingTopupCount > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #0f172a)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 32,
          border: '2px solid #f59e0b',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(245, 158, 11, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Lock size={24} color="#fbbf24" />
              </div>
              <div>
                <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  صفقات بانتظار الشحن الأول
                  <button
                    onClick={() => setShowPendingTopupInfo(!showPendingTopupInfo)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="شرح"
                  >
                    <AlertCircle size={18} color="#fbbf24" />
                  </button>
                </h3>
                <p style={{ color: '#000000', fontSize: 13, margin: '4px 0 0' }}>
                  {pendingTopupCount} {pendingTopupCount === 1 ? 'صفقة' : 'صفقة'} مغلقة بانتظار شحن المطعم لرصيده
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#64748b', fontSize: 12 }}>العمولة المتوقعة</div>
              <div style={{ color: '#fbbf24', fontSize: 28, fontWeight: 900 }}>
                {potentialPendingEarnings} AED
              </div>
            </div>
          </div>

          {/* Info Popup */}
          {showPendingTopupInfo && (
            <div style={{
              padding: 16,
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: 12,
              border: '1px solid #f59e0b',
              marginBottom: 16,
            }}>
              <h4 style={{ color: '#fbbf24', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                ℹ️ لماذا هذه الصفقات معلقة؟
              </h4>
              <ul style={{ color: '#000000', fontSize: 13, lineHeight: 1.8, margin: 0, paddingRight: 20 }}>
                <li>اختر المطعم خيار "الدفع بعد الشحن الأول" عند التسجيل</li>
                <li>لن يتم صرف العمولة (20 AED) حتى يشحن المطعم محفظته لأول مرة</li>
                <li>بمجرد الشحن الأول، ستتحول العمولة إلى "مستحقة" تلقائياً</li>
                <li>يمكنك متابعة المطعم وحثه على الشحن لتفعيل عمولتك</li>
              </ul>
            </div>
          )}

          {/* Pending Deals List */}
          <div style={{ display: 'grid', gap: 8 }}>
            {pendingTopupDeals.slice(0, 5).map(deal => (
              <div
                key={deal.id}
                style={{
                  padding: 12,
                  background: 'rgba(245, 158, 11, 0.05)',
                  borderRadius: 10,
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Lock size={16} color="#fbbf24" />
                  <div>
                    <div style={{ color: '#000000', fontSize: 14, fontWeight: 700 }}>
                      {deal.name}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>
                      {deal.area || '—'}
                    </div>
                  </div>
                </div>
                <div style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700 }}>
                  20 AED
                </div>
              </div>
            ))}
            {pendingTopupCount > 5 && (
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, paddingTop: 8 }}>
                و {pendingTopupCount - 5} صفقات أخرى...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 16, 
        marginBottom: 32 
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #10b981',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#6ee7b7', fontSize: 12, marginBottom: 8 }}>إجمالي الأرباح</p>
          <p style={{ color: '#34d399', fontSize: 32, fontWeight: 900 }}>{totalEarnings.toFixed(0)} AED</p>
          {milestoneEarnings > 0 && (
            <p style={{ color: '#10b981', fontSize: 11, marginTop: 4 }}>
              يشمل {milestoneEarnings.toFixed(0)} AED مكافآت
            </p>
          )}
        </div>
      </div>

      {/* My Contacts by Area - Simple SVG Map */}
      <div style={{
        background: '#ffffff',
        borderRadius: isMobile ? 10 : 16,
        border: '1px solid #e2e8f0',
        padding: isMobile ? 16 : 24,
        marginBottom: 32,
      }}>
        <h3 style={{ color: '#000000', fontSize: isMobile ? 14 : 18, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>📍</span>
          جهات اتصالي حسب المنطقة
        </h3>

        {districtCounts.length > 0 ? (
          <div>
            <div style={{ 
              position: 'relative', 
              width: '100%', 
              height: isMobile ? '250px' : '400px',
              marginBottom: 24,
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
            }}>
              <DistrictMap districtCounts={districtCounts} />
            </div>

            {/* District List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {districtCounts.slice(0, 8).map(([district, count], index) => {
                const maxCount = districtCounts[0][1];
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={district} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: '50%', 
                      background: index === 0 ? '#4ecdc4' : index === 1 ? '#10b981' : '#94a3b8',
                      color: '#ffffff',
                      fontSize: 10,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {index + 1}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: '#000000', fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>
                          {district}
                        </span>
                        <span style={{ color: '#4ecdc4', fontSize: isMobile ? 12 : 14, fontWeight: 700 }}>
                          {count}
                        </span>
                      </div>
                      <div style={{ height: isMobile ? 6 : 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${percentage}%`,
                          background: index === 0 ? '#4ecdc4' : index === 1 ? '#10b981' : '#94a3b8',
                          borderRadius: 4,
                        }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center' }}>
            لا توجد بيانات كافية
          </p>
        )}
      </div>

      {/* Progress Tracker & Top Products */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: isMobile ? 12 : 16, 
        marginBottom: 32 
      }}>
        {/* Restaurant Onboarding Progress */}
        <div style={{
          background: '#ffffff',
          borderRadius: isMobile ? 10 : 16,
          padding: isMobile ? 16 : 24,
          border: '1px solid #e2e8f0',
        }}>
          <h3 style={{ color: '#000000', fontSize: isMobile ? 13 : 16, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 16 }}>📊</span>
            تقدم تسجيل المطاعم
          </h3>
          
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#000000', fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>
                {editedContactsCount}/{totalDeals}
              </span>
              <span style={{ color: '#4ecdc4', fontSize: isMobile ? 12 : 14, fontWeight: 700 }}>
                {totalDeals > 0 ? Math.round((editedContactsCount / totalDeals) * 100) : 0}% مكتمل
              </span>
            </div>
            <div style={{
              height: isMobile ? 8 : 12,
              background: '#e2e8f0',
              borderRadius: 4,
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${totalDeals > 0 ? (editedContactsCount / totalDeals) * 100 : 0}%`,
                background: 'linear-gradient(90deg, #4ecdc4, #10b981)',
                borderRadius: 4,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{
              padding: 10,
              background: '#f8fafc',
              borderRadius: 8,
              textAlign: 'center',
            }}>
              <div style={{ color: '#4ecdc4', fontSize: isMobile ? 18 : 24, fontWeight: 900 }}>{editedContactsCount}</div>
              <div style={{ color: '#64748b', fontSize: 10, marginTop: 2 }}>تم تعديلها</div>
            </div>
            <div style={{
              padding: 10,
              background: '#f8fafc',
              borderRadius: 8,
              textAlign: 'center',
            }}>
              <div style={{ color: '#000000', fontSize: isMobile ? 18 : 24, fontWeight: 900 }}>{totalDeals}</div>
              <div style={{ color: '#64748b', fontSize: 10, marginTop: 2 }}>إجمالي جهات الاتصال</div>
            </div>
          </div>
        </div>

        {/* Top 3 Most Requested Products */}
        {topProducts.length > 0 && (
          <div style={{
            background: '#ffffff',
            borderRadius: isMobile ? 10 : 16,
            padding: isMobile ? 16 : 24,
            border: '1px solid #e2e8f0',
          }}>
            <h3 style={{ color: '#000000', fontSize: isMobile ? 13 : 16, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>📊</span>
              أكثر 3 منتجات طلباً
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {topProducts.map((product, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: 16,
                  background: index === 0 ? 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(16, 185, 129, 0.05))' : '#f8fafc',
                  border: index === 0 ? '2px solid #4ecdc4' : '1px solid #e2e8f0',
                  borderRadius: 12,
                }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: index === 0 ? '#4ecdc4' : index === 1 ? '#10b981' : '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontSize: 18,
                    fontWeight: 900,
                  }}>
                    #{index + 1}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                      {product.name}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 13 }}>
                      {product.count} {product.count === 1 ? 'مطعم' : 'مطاعم'}
                    </div>
                  </div>

                  {index === 0 && (
                    <span style={{ fontSize: 24 }}>🥇</span>
                  )}
                  {index === 1 && (
                    <span style={{ fontSize: 24 }}>🥈</span>
                  )}
                  {index === 2 && (
                    <span style={{ fontSize: 24 }}>🥉</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent Deals */}
      {recentDeals.length > 0 && (
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}>
          <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0' }}>
            <h2 style={{ color: '#000000', fontSize: 18, fontWeight: 700, margin: 0 }}>
              آخر الصفقات
            </h2>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#ffffff' }}>
                  <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>المطعم</th>
                  <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>المنطقة</th>
                  <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>الحالة</th>
                  <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>العمولة</th>
                </tr>
              </thead>
              <tbody>
                {recentDeals.map(deal => {
                  const isCommissionPending = deal.commissionPending === true;
                  
                  return (
                    <tr key={deal.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '12px 16px', color: '#000000', fontWeight: 700 }}>{deal.name}</td>
                      <td style={{ padding: '12px 16px', color: '#000000' }}>{deal.area || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <StatusBadge status={deal.status} />
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {isCommissionPending ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid #f59e0b',
                            borderRadius: 8,
                            width: 'fit-content',
                          }}>
                            <Lock size={14} color="#fbbf24" />
                            <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700 }}>
                              بانتظار الشحن
                            </span>
                          </div>
                        ) : deal.status === 'deal_closed' ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid #10b981',
                            borderRadius: 8,
                            width: 'fit-content',
                          }}>
                            <Check size={14} color="#34d399" />
                            <span style={{ color: '#34d399', fontSize: 11, fontWeight: 700 }}>
                              مستحقة
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: 11 }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
